/**
 * SmartScheduler.js
 *
 * AI-powered appliance scheduling component for Dagens Elpris.
 * Recommends the cheapest and most stable time windows to run
 * household appliances based on Nord Pool hourly prices.
 *
 * Improvements over v1:
 * - Percentile-based scoring (price relative to today's range, not raw öre)
 * - Volatility penalty (prefer stable windows over lucky dips)
 * - Momentum awareness (prefer "valley" windows surrounded by higher prices)
 * - Interactive price timeline bar chart (recharts, already in deps)
 * - AI-generated Swedish-language insights via GPT-5.4-mini backend endpoint
 * - EV battery model presets with configurable capacity
 * - Hot water heater & Custom appliance tabs
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Tab } from '@headlessui/react';
import {
    BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer
} from 'recharts';
import {
    BoltIcon,
    ClockIcon,
    ChartBarIcon,
    CurrencyEuroIcon,
    CalendarDaysIcon,
    SunIcon,
    MoonIcon,
    ArrowPathIcon,
    SparklesIcon as SparklesOutlineIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
    TruckIcon as CarIcon,
    HomeIcon,
    SparklesIcon,
    FireIcon,
    WrenchScrewdriverIcon
} from '@heroicons/react/24/solid';
import axios from 'axios';

// ─────────────────────────────────────────────────────────────────────────────
// Error fallback
// ─────────────────────────────────────────────────────────────────────────────
const ErrorFallback = ({ error, resetErrorBoundary }) => (
    <div className="p-4 bg-red-500/10 rounded-lg">
        <h2 className="text-xl font-bold mb-2 text-white">Något gick fel:</h2>
        <pre className="text-sm text-red-200">{error.message}</pre>
        <button
            onClick={resetErrorBoundary}
            className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white"
        >
            Försök igen
        </button>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────
const APPLIANCE_CONFIG = {
    dishwasher: {
        title: 'Diskmaskin',
        icon: HomeIcon,
        duration: 2,
        power: 1.5,
        avoidNight: false,
        tips: [
            'Eco-program kör längre men använder menos el – perfekt på natten.',
            'Ladda full maskin för bästa energieffektivitet.',
            'Diskmaskinen förbrukar mest el under uppvärmningen – kör den i ett svep.'
        ]
    },
    laundry: {
        title: 'Tvättmaskin',
        icon: ArrowPathIcon,
        duration: 2,
        power: 2.0,
        avoidNight: false,
        tips: [
            '30–40 °C-tvätt sparar upp till 60 % el jämfört med 60 °C.',
            'Fyll maskinen full för bästa energieffektivitet per plagget.',
            'Torktumlaren drar mer el – häng gärna tvätten när priserna är höga.'
        ]
    },
    pool: {
        title: 'Poolpump',
        icon: WrenchScrewdriverIcon,
        duration: 6,
        power: 1.2,
        avoidNight: false,
        tips: [
            'Dela upp filtertiden i kortare intervall om möjligt.',
            'Nattdrift är optimalt – vatten cirkulerar utan att avdunsta av sol.',
            'Rent filter sänker pumpens energiförbrukning märkbart.'
        ]
    },
    heating: {
        title: 'Uppvärmning / VP',
        icon: FireIcon,
        duration: 4,
        power: 3.5,
        avoidNight: false,
        tips: [
            'Förvärm huset under billiga timmar – huset håller värmen länge.',
            'Sänk thermostat 1 °C sparar ca 5–10 % på uppvärmningskostnaden.',
            'Tilläggsisolering i krypgrund ger snabb återbetalning.'
        ]
    },
    hot_water: {
        title: 'Varmvattenberedare',
        icon: FireIcon,
        duration: 2,
        power: 2.0,
        avoidNight: false,
        tips: [
            'En 200-liters beredare värmd på natten räcker för de flesta hushåll.',
            'Håll minst 55 °C mot legionellabakterie.',
            'Isolera varmvattenrören nära beredaren för att minska värmeförlust.'
        ]
    }
};

// Common EV models sold in Sweden
const EV_PRESETS = [
    { label: 'Anpassad',                  capacity: 0,   efficiency: 0.92 },
    { label: 'Tesla Model 3 Standard',    capacity: 60,  efficiency: 0.92 },
    { label: 'Tesla Model 3 Long Range',  capacity: 82,  efficiency: 0.91 },
    { label: 'VW ID.4 / ID.3',            capacity: 77,  efficiency: 0.92 },
    { label: 'Volvo EX30',                capacity: 51,  efficiency: 0.93 },
    { label: 'Volvo XC40 / C40',          capacity: 82,  efficiency: 0.91 },
    { label: 'BMW iX / i4',               capacity: 83,  efficiency: 0.91 },
    { label: 'Kia EV6 / Ioniq 6',         capacity: 77,  efficiency: 0.92 },
    { label: 'Nissan Leaf 40 kWh',        capacity: 40,  efficiency: 0.91 },
    { label: 'Polestar 2 Long Range',     capacity: 82,  efficiency: 0.92 },
    { label: 'Porsche Taycan',            capacity: 93,  efficiency: 0.90 }
];

// ─────────────────────────────────────────────────────────────────────────────
// Core scheduling algorithm
// ─────────────────────────────────────────────────────────────────────────────

/** Compute descriptive statistics for a price array */
const analyzePrices = (prices) => {
    const valid = prices.filter(p => p !== null && p !== undefined);
    if (valid.length === 0) return null;
    const sorted = [...valid].sort((a, b) => a - b);
    const n = sorted.length;
    const avg = valid.reduce((s, p) => s + p, 0) / n;
    const variance = valid.reduce((s, p) => s + Math.pow(p - avg, 2), 0) / n;
    return {
        min:    sorted[0],
        max:    sorted[n - 1],
        avg,
        p10:    sorted[Math.floor(n * 0.10)],
        p25:    sorted[Math.floor(n * 0.25)],
        p50:    sorted[Math.floor(n * 0.50)],
        p75:    sorted[Math.floor(n * 0.75)],
        p90:    sorted[Math.floor(n * 0.90)],
        stdDev: Math.sqrt(variance),
        sorted  // keep sorted array for percentile lookup
    };
};

/** Return the percentile rank (0–100) of a value in a sorted array */
const getPercentile = (value, sortedArr) => {
    if (!sortedArr || sortedArr.length === 0) return 50;
    const below = sortedArr.filter(p => p <= value).length;
    return Math.round((below / sortedArr.length) * 100);
};

/** Format a human-readable time range label */
const formatTimeLabel = (startHour, endHour, totalHours, hasTomorrowData = false) => {
    const startDay     = Math.floor(startHour / 24);
    const endDay       = Math.floor(endHour   / 24);
    const startHourDay = startHour % 24;
    const endHourDay   = (endHour + 1) % 24;
    const startTime    = String(startHourDay).padStart(2, '0') + ':00';
    const endTime      = String(endHourDay).padStart(2, '0') + ':00';
    const dayLabels    = ['Idag', 'Imorgon', 'I övermorgon'];
    let startLabel = '';
    let endLabel   = '';
    if (hasTomorrowData && (totalHours > 24 || startDay !== endDay)) {
        startLabel = startDay < dayLabels.length ? ' (' + dayLabels[startDay] + ')' : ' (+' + startDay + 'd)';
        if (startDay !== endDay) {
            endLabel = endDay < dayLabels.length ? ' (' + dayLabels[endDay] + ')' : ' (+' + endDay + 'd)';
        }
    }
    return startTime + startLabel + ' \u2013 ' + endTime + endLabel;
};

/**
 * Find and rank all valid time windows.
 *
 * Scoring factors (high score = better):
 *  1. Price percentile within all available hours (primary – 0-100 pts)
 *  2. Stability bonus – low intra-window stdDev (up to +15 pts)
 *  3. Valley bonus – surrounded by more expensive hours (+up to 10 pts)
 *  4. Soft night-avoidance penalty (-40 pts)
 *  5. Hard out-of-range penalty (-200 pts)
 */
const calculateOptimalTimes = (hourlyPrices, duration, constraints = {}, hasTomorrowData = false) => {
    if (!hourlyPrices || hourlyPrices.length === 0 || duration < 1) return [];

    const {
        avoidNightHours = false,
        maxStartHour    = null,
        minStartHour    = 0
    } = constraints;

    const stats = analyzePrices(hourlyPrices);
    if (!stats) return [];

    const { sorted: sortedAll, min: gMin, max: gMax } = stats;
    const priceRange  = gMax - gMin || 1;
    const effectiveMax = maxStartHour !== null
        ? Math.min(maxStartHour, hourlyPrices.length - duration)
        : hourlyPrices.length - duration;

    const windows = [];

    for (let i = 0; i <= hourlyPrices.length - duration; i++) {
        const slice = hourlyPrices.slice(i, i + duration);
        if (slice.some(p => p === null || p === undefined)) continue;

        const avg   = slice.reduce((s, p) => s + p, 0) / duration;
        const total = slice.reduce((s, p) => s + p, 0);

        // Intra-window standard deviation (volatility)
        const wVar   = slice.reduce((s, p) => s + Math.pow(p - avg, 2), 0) / duration;
        const wStdDev = Math.sqrt(wVar);
        const relStd  = priceRange > 0 ? wStdDev / priceRange : 0; // 0 = perfectly stable

        // Price percentile (lower = cheaper = better)
        const percentile = getPercentile(avg, sortedAll);

        // Momentum / valley detection
        const before    = i > 0 ? hourlyPrices[i - 1] : avg;
        const after     = (i + duration) < hourlyPrices.length ? hourlyPrices[i + duration] : avg;
        const validBefore = before !== null && before !== undefined;
        const validAfter  = after  !== null && after  !== undefined;
        const dropIn      = validBefore && before > avg;
        const riseOut     = validAfter  && after  > avg;

        // ── Scoring ──────────────────────────────────────────────────────────
        let score = (100 - percentile) * 1.0;                    // 0-100 from percentile
        score += (1 - Math.min(relStd * 2, 1)) * 15;            // stability bonus 0-15
        if (dropIn)           score += 3;
        if (riseOut)          score += 3;
        if (dropIn && riseOut) score += 4;                       // full valley +4 extra

        const hourInDay = i % 24;
        const isNight   = hourInDay >= 22 || hourInDay < 6;
        if (avoidNightHours && isNight) score -= 40;

        const outsideRange = (i < minStartHour) || (i > effectiveMax);
        if (outsideRange) score -= 200;

        windows.push({
            startHour:    i,
            endHour:      i + duration - 1,
            averagePrice: avg,
            totalCost:    total,
            score,
            percentile,
            stdDev:       wStdDev.toFixed(1),
            isNight,
            isValley:     dropIn && riseOut,
            timeLabel:    formatTimeLabel(i, i + duration - 1, hourlyPrices.length, hasTomorrowData)
        });
    }

    return windows.sort((a, b) => {
        const diff = b.score - a.score;
        if (Math.abs(diff) > 1) return diff;
        return a.averagePrice - b.averagePrice;
    });
};

const estimateSavings = (best, worst, energyKwh = 1) => {
    if (!best || !worst) return 0;
    return ((worst.averagePrice - best.averagePrice) * energyKwh / 100).toFixed(2);
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const priceBarColor = (price, stats) => {
    if (!stats) return '#6b7280';
    const pct = stats.max > stats.min ? (price - stats.min) / (stats.max - stats.min) : 0.5;
    if (pct < 0.20) return '#22c55e';
    if (pct < 0.45) return '#86efac';
    if (pct < 0.65) return '#fcd34d';
    if (pct < 0.85) return '#f97316';
    return '#ef4444';
};

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-gray-800 border border-white/20 rounded px-2 py-1 text-xs text-white shadow-lg">
            <p className="font-semibold">{d.label}</p>
            <p>{(d.price || 0).toFixed(1)} öre/kWh</p>
            {d.isRecommended && <p className="text-green-400 font-medium">★ Rekommenderad</p>}
            {d.isNow && <p className="text-blue-400">▶ Nu</p>}
        </div>
    );
};

/** Mini price bar chart with highlighted recommendation window */
const PriceTimelineChart = ({ hourlyPrices, topWindow, stats }) => {
    const hasTomorrow = !!(hourlyPrices && hourlyPrices.length > 24);

    const chartData = useMemo(() => {
        if (!hourlyPrices || hourlyPrices.length === 0) return [];
        const nowHour = new Date().getHours();
        return hourlyPrices.map((price, i) => ({
            // Give day-boundary bars a distinct label so ReferenceLine can target index 24 exactly
            label:         i === 0 ? 'Idag' : i === 24 ? 'Imorgon' : String(i % 24).padStart(2, '0') + ':00',
            price:         price !== null && price !== undefined ? price : 0,
            isRecommended: !!(topWindow && i >= topWindow.startHour && i <= topWindow.endHour),
            isNow:         i === nowHour,
            isPast:        i < nowHour && i < 24
        }));
    }, [hourlyPrices, topWindow]);

    if (chartData.length === 0) return null;

    // Show a tick every ~8 hours so labels don't crowd
    const interval = hasTomorrow ? 7 : 3;

    return (
        <div className="bg-white/5 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-white/60 flex items-center gap-1.5">
                    <ChartBarIcon className="h-4 w-4 text-blue-400" />
                    {hasTomorrow ? 'Prisutveckling idag & imorgon (öre/kWh)' : 'Prisutveckling idag (öre/kWh)'}
                </h5>
                <div className="flex gap-3 text-xs text-white/40">
                    <span><span style={{display:'inline-block',width:8,height:8,borderRadius:2,background:'#22c55e',marginRight:4}} />Rekommenderad</span>
                    <span><span style={{display:'inline-block',width:8,height:8,borderRadius:2,background:'#60a5fa',marginRight:4}} />Nu</span>
                </div>
            </div>
            <ResponsiveContainer width="100%" height={120}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 16, left: 0 }} barCategoryGap="2%">
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 8, fill: 'rgba(255,255,255,0.3)' }}
                        interval={interval}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis hide domain={['auto', 'auto']} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    {hasTomorrow && (
                        <ReferenceLine
                            x="Imorgon"
                            stroke="rgba(255,255,255,0.2)"
                            strokeDasharray="4 3"
                        />
                    )}
                    <Bar dataKey="price" radius={[2, 2, 0, 0]}>
                        {chartData.map((entry, idx) => (
                            <Cell
                                key={idx}
                                fill={
                                    entry.isRecommended ? '#22c55e' :
                                    entry.isNow        ? '#60a5fa' :
                                    priceBarColor(entry.price, stats)
                                }
                                opacity={entry.isPast ? 0.25 : 1}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

/** Single recommendation result card */
const RecommendationCard = ({ slot, index, duration, power, currencySymbol, accentRgb }) => {
    const totalCost  = (slot.totalCost * power / 100).toFixed(2);
    const isNegative = slot.averagePrice < 0;

    const qualityLabel =
        slot.percentile <= 20 ? 'Topp 20 % billigast' :
        slot.percentile <= 40 ? 'Bra pris' :
        slot.percentile <= 60 ? 'Genomsnittspris' :
        slot.percentile <= 80 ? 'Högt pris' : 'Dyrt pris';

    const qualityColor =
        slot.percentile <= 20 ? '#22c55e' :
        slot.percentile <= 40 ? '#86efac' :
        slot.percentile <= 60 ? '#fcd34d' :
        slot.percentile <= 80 ? '#f97316' : '#ef4444';

    const cardStyle = index === 0
        ? { borderColor: 'rgba(' + accentRgb + ',0.5)', backgroundColor: 'rgba(' + accentRgb + ',0.08)' }
        : { borderColor: 'rgba(255,255,255,0.15)', backgroundColor: 'rgba(255,255,255,0.04)' };

    return (
        <div
            className="p-4 rounded-xl border transition-all hover:scale-[1.02] cursor-default"
            style={cardStyle}
        >
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/60">
                    {index === 0 ? 'Bästa tid' : 'Alternativ ' + (index + 1)}
                </span>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                    {index === 0 && <SparklesIcon className="h-4 w-4 text-yellow-400" />}
                    {slot.isValley && (
                        <span className="text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">Dalgång</span>
                    )}
                    {isNegative && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">Betalt!</span>
                    )}
                </div>
            </div>

            <p className="text-lg font-bold text-white mb-3">{slot.timeLabel}</p>

            <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                    <span className="text-white/50">Snittspris:</span>
                    <span className="font-semibold" style={{ color: isNegative ? '#22c55e' : '#fff' }}>
                        {slot.averagePrice.toFixed(1)} {currencySymbol}/kWh
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-white/50">Totalkostnad:</span>
                    <span className="font-semibold" style={{ color: isNegative ? '#22c55e' : '#fff' }}>
                        {totalCost} kr
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-white/50">Stabilitet:</span>
                    <span className="text-white/80">&plusmn;{slot.stdDev} öre</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-white/50">Prisläge:</span>
                    <span className="font-medium" style={{ color: qualityColor }}>{qualityLabel}</span>
                </div>
            </div>
        </div>
    );
};

/** AI insight block – shows GPT-generated Swedish text from backend */
const AIInsightPanel = ({ insight, loading }) => {
    if (loading) {
        return (
            <div className="mt-4 bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 animate-pulse">
                <div className="flex items-center gap-2 mb-3">
                    <SparklesOutlineIcon className="h-4 w-4 text-purple-400" />
                    <span className="text-sm font-medium text-purple-300">AI-analys laddas…</span>
                </div>
                <div className="space-y-2">
                    <div className="h-3 bg-white/10 rounded w-3/4" />
                    <div className="h-3 bg-white/10 rounded w-1/2" />
                    <div className="h-3 bg-white/10 rounded w-2/3" />
                </div>
            </div>
        );
    }
    if (!insight) return null;

    return (
        <div className="mt-4 bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <SparklesOutlineIcon className="h-4 w-4 text-purple-400" />
                <span className="text-sm font-semibold text-purple-300">AI-rekommendation (GPT-4o-mini)</span>
            </div>
            <div className="space-y-2 text-sm">
                {insight.insight && (
                    <div className="flex items-start gap-2">
                        <CheckCircleIcon className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <p className="text-white/90">{insight.insight}</p>
                    </div>
                )}
                {insight.context && (
                    <div className="flex items-start gap-2">
                        <InformationCircleIcon className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <p className="text-white/65">{insight.context}</p>
                    </div>
                )}
                {insight.tip && (
                    <div className="flex items-start gap-2">
                        <BoltIcon className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <p className="text-blue-200">{insight.tip}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom hook: fetch AI insights from backend (debounced)
// ─────────────────────────────────────────────────────────────────────────────
const useAIInsight = (hourlyPrices, applianceType, duration, topWindow, zone, hasTomorrowPrices) => {
    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(false);
    const prevKeyRef            = useRef('');

    useEffect(() => {
        if (!topWindow || !hourlyPrices || hourlyPrices.length === 0) return;
        const key = zone + '|' + applianceType + '|' + duration + '|' + topWindow.startHour;
        if (key === prevKeyRef.current) return;
        prevKeyRef.current = key;

        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await axios.post('/api/smart-scheduler-insights', {
                    zone,
                    hourlyPrices,
                    applianceType,
                    duration,
                    topWindow: {
                        startHour:    topWindow.startHour,
                        averagePrice: topWindow.averagePrice,
                        percentile:   topWindow.percentile,
                        timeLabel:    topWindow.timeLabel
                    },
                    hasTomorrowData: hasTomorrowPrices
                });
                setInsight(res.data);
            } catch {
                setInsight(null);
            } finally {
                setLoading(false);
            }
        }, 700);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topWindow?.startHour, applianceType, duration, zone]);

    return { insight, loading };
};

// ─────────────────────────────────────────────────────────────────────────────
// EVChargingScheduler
// ─────────────────────────────────────────────────────────────────────────────
const EVChargingScheduler = ({ hourlyPrices, selectedRegion, currencySymbol, hasTomorrowPrices, stats }) => {
    const [presetIdx,    setPresetIdx]    = useState(1);
    const [batteryLevel, setBatteryLevel] = useState(20);
    const [targetLevel,  setTargetLevel]  = useState(80);
    const [chargerPower, setChargerPower] = useState(11);
    const [neededBy,     setNeededBy]     = useState('07:00');
    const [customCap,    setCustomCap]    = useState(75);

    const preset          = EV_PRESETS[presetIdx];
    const batteryCapacity = preset.capacity > 0 ? preset.capacity : customCap;
    const efficiency      = preset.efficiency;

    const chargingTime = useMemo(() => {
        const kwhNeeded = ((targetLevel - batteryLevel) / 100) * batteryCapacity / efficiency;
        return Math.max(1, Math.ceil(kwhNeeded / chargerPower));
    }, [batteryLevel, targetLevel, batteryCapacity, chargerPower, efficiency]);

    const energyKwh = useMemo(
        () => ((targetLevel - batteryLevel) / 100) * batteryCapacity / efficiency,
        [batteryLevel, targetLevel, batteryCapacity, efficiency]
    );

    const optimalTimes = useMemo(() => {
        if (!hourlyPrices || hourlyPrices.length === 0) return [];
        const [neededHour] = neededBy.split(':').map(Number);
        const currentHour  = new Date().getHours();
        const target = neededHour > currentHour ? neededHour : 24 + neededHour;
        const maxStart = Math.max(currentHour, Math.min(target - chargingTime, hourlyPrices.length - chargingTime));
        return calculateOptimalTimes(
            hourlyPrices, chargingTime,
            { maxStartHour: maxStart, minStartHour: currentHour },
            hasTomorrowPrices
        ).slice(0, 3);
    }, [hourlyPrices, chargingTime, neededBy, hasTomorrowPrices]);

    const worstTime = useMemo(() => {
        if (!hourlyPrices) return null;
        const all = calculateOptimalTimes(hourlyPrices, chargingTime, {}, hasTomorrowPrices);
        return all[all.length - 1];
    }, [hourlyPrices, chargingTime, hasTomorrowPrices]);

    const savings = estimateSavings(optimalTimes[0], worstTime, energyKwh);
    const topSlot = optimalTimes[0] || null;
    const { insight, loading: aiLoading } = useAIInsight(
        hourlyPrices, 'ev', chargingTime, topSlot, selectedRegion, hasTomorrowPrices
    );

    return (
        <div className="space-y-6">
            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Bilmodell / batteri</label>
                    <select
                        value={presetIdx}
                        onChange={(e) => setPresetIdx(parseInt(e.target.value))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                        style={{ color: '#fff' }}
                    >
                        {EV_PRESETS.map((p, i) => (
                            <option key={i} value={i} style={{ background: '#1f2937', color: '#fff' }}>
                                {p.label}{p.capacity > 0 ? ' (' + p.capacity + ' kWh)' : ''}
                            </option>
                        ))}
                    </select>
                </div>

                {preset.capacity === 0 && (
                    <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">Batterikapacitet (kWh)</label>
                        <input
                            type="number" min="10" max="150" value={customCap}
                            onChange={(e) => setCustomCap(Math.max(10, parseInt(e.target.value) || 75))}
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                            style={{ colorScheme: 'dark' }}
                        />
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                        Nuvarande: <strong className="text-white">{batteryLevel}%</strong>
                    </label>
                    <input
                        type="range" min="0" max="99" value={batteryLevel}
                        onChange={(e) => {
                            const v = parseInt(e.target.value);
                            setBatteryLevel(v);
                            if (targetLevel <= v) setTargetLevel(Math.min(100, v + 10));
                        }}
                        className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-white/30 mt-1"><span>0%</span><span>100%</span></div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                        Önskad: <strong className="text-white">{targetLevel}%</strong>
                    </label>
                    <input
                        type="range" min={Math.min(batteryLevel + 1, 100)} max="100" value={targetLevel}
                        onChange={(e) => setTargetLevel(parseInt(e.target.value))}
                        className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-white/30 mt-1"><span>{batteryLevel + 1}%</span><span>100%</span></div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Laddeffekt (kW)</label>
                    <select
                        value={chargerPower}
                        onChange={(e) => setChargerPower(parseFloat(e.target.value))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                        style={{ color: '#fff' }}
                    >
                        {[[3.7,'Vanligt uttag'],[7.4,'Typ 2'],[11,'Tre-fas'],[22,'Snabbladdare'],[50,'DC'],[150,'DC HPC']].map(([kw, lbl]) => (
                            <option key={kw} value={kw} style={{ background: '#1f2937', color: '#fff' }}>
                                {kw} kW ({lbl})
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Klar senast</label>
                    <input
                        type="time" value={neededBy}
                        onChange={(e) => setNeededBy(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                        style={{ colorScheme: 'dark' }}
                    />
                </div>
            </div>

            {/* Summary row */}
            <div className="flex flex-wrap gap-4 text-sm text-white/50">
                <span>Energi: <strong className="text-white">{energyKwh.toFixed(1)} kWh</strong></span>
                <span>•</span>
                <span>Laddtid: <strong className="text-white">{chargingTime} h</strong></span>
                <span>•</span>
                <span>Verkningsgrad: <strong className="text-white">{Math.round(efficiency * 100)} %</strong></span>
                <span>•</span>
                <span>Batteri: <strong className="text-white">{batteryCapacity} kWh</strong></span>
            </div>

            {/* Price chart */}
            <PriceTimelineChart hourlyPrices={hourlyPrices} topWindow={topSlot} stats={stats} />

            {/* Results */}
            <div className="bg-white/5 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <BoltIcon className="h-5 w-5 text-green-400" />
                    Laddningsrekommendationer
                </h4>

                {optimalTimes.length === 0 ? (
                    <div className="text-center py-8">
                        <ExclamationTriangleIcon className="h-10 w-10 mx-auto text-yellow-400 mb-3" />
                        <p className="text-white/70">Inga tillgängliga tider. Justera laddeffekten eller deadline.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                            {optimalTimes.map((slot, i) => (
                                <RecommendationCard
                                    key={i} slot={slot} index={i}
                                    duration={chargingTime} power={chargerPower}
                                    currencySymbol={currencySymbol} accentRgb="34,197,94"
                                />
                            ))}
                        </div>

                        {parseFloat(savings) > 0 && (
                            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
                                <CurrencyEuroIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                                <span className="text-green-300 text-sm">
                                    Potentiell besparing: <strong>{savings} kr</strong> jämfört med sämsta alternativet
                                </span>
                            </div>
                        )}

                        <AIInsightPanel insight={insight} loading={aiLoading} />
                    </>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ApplianceScheduler (generic – dishwasher, laundry, pool, heating, hot_water)
// ─────────────────────────────────────────────────────────────────────────────
const ApplianceScheduler = ({ hourlyPrices, selectedRegion, currencySymbol, applianceKey, hasTomorrowPrices, stats }) => {
    const config   = APPLIANCE_CONFIG[applianceKey] || APPLIANCE_CONFIG.dishwasher;
    const [duration,   setDuration]  = useState(config.duration);
    const [avoidNight, setAvoidNight] = useState(config.avoidNight);
    const power = config.power;
    const IconComp = config.icon;

    const optimalTimes = useMemo(() => {
        if (!hourlyPrices || hourlyPrices.length === 0) return [];
        return calculateOptimalTimes(
            hourlyPrices, duration,
            { avoidNightHours: avoidNight },
            hasTomorrowPrices
        ).slice(0, 3);
    }, [hourlyPrices, duration, avoidNight, hasTomorrowPrices]);

    const worstTime = useMemo(() => {
        if (!hourlyPrices) return null;
        const all = calculateOptimalTimes(hourlyPrices, duration, {}, hasTomorrowPrices);
        return all[all.length - 1];
    }, [hourlyPrices, duration, hasTomorrowPrices]);

    const savings = estimateSavings(optimalTimes[0], worstTime, duration * power);
    const topSlot = optimalTimes[0] || null;
    const { insight, loading: aiLoading } = useAIInsight(
        hourlyPrices, applianceKey, duration, topSlot, selectedRegion, hasTomorrowPrices
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-6 items-end">
                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Körtid (timmar)</label>
                    <input
                        type="number" min="1" max="24" value={duration}
                        onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-28 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                        style={{ colorScheme: 'dark' }}
                    />
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                        type="checkbox" checked={avoidNight}
                        onChange={(e) => setAvoidNight(e.target.checked)}
                        className="w-4 h-4 rounded"
                    />
                    <span className="text-sm text-white/70">Undvik nattimmar (22–06)</span>
                </label>
            </div>

            <div className="flex flex-wrap gap-3 text-sm text-white/50">
                <span>Effekt: <strong className="text-white">{power} kW</strong></span>
                <span>•</span>
                <span>Energi: <strong className="text-white">{(duration * power).toFixed(1)} kWh</strong></span>
            </div>

            <PriceTimelineChart hourlyPrices={hourlyPrices} topWindow={topSlot} stats={stats} />

            <div className="bg-white/5 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <IconComp className="h-5 w-5 text-blue-400" />
                    {config.title} – Rekommendationer
                </h4>

                {optimalTimes.length === 0 ? (
                    <div className="text-center py-8">
                        <ExclamationTriangleIcon className="h-10 w-10 mx-auto text-yellow-400 mb-3" />
                        <p className="text-white/70">Inga tider hittades. Justera körtiden.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                            {optimalTimes.map((slot, i) => (
                                <RecommendationCard
                                    key={i} slot={slot} index={i}
                                    duration={duration} power={power}
                                    currencySymbol={currencySymbol} accentRgb="59,130,246"
                                />
                            ))}
                        </div>

                        {parseFloat(savings) > 0 && (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-center gap-2">
                                <CurrencyEuroIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                                <span className="text-blue-300 text-sm">
                                    Potentiell besparing: <strong>{savings} kr</strong> per körning
                                </span>
                            </div>
                        )}

                        {config.tips && (
                            <div className="mt-4 p-4 bg-white/5 rounded-lg">
                                <p className="text-sm font-semibold text-white mb-2">Tips:</p>
                                <ul className="space-y-1">
                                    {config.tips.map((tip, i) => (
                                        <li key={i} className="text-sm text-white/55 flex items-start gap-2">
                                            <span className="text-blue-400 mt-0.5 flex-shrink-0">•</span>{tip}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <AIInsightPanel insight={insight} loading={aiLoading} />
                    </>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// CustomApplianceScheduler
// ─────────────────────────────────────────────────────────────────────────────
const CustomApplianceScheduler = ({ hourlyPrices, selectedRegion, currencySymbol, hasTomorrowPrices, stats }) => {
    const [duration,   setDuration]   = useState(2);
    const [power,      setPower]      = useState(1.5);
    const [avoidNight, setAvoidNight] = useState(false);
    const [deadlineOn, setDeadlineOn] = useState(false);
    const [deadline,   setDeadline]   = useState('12:00');

    const optimalTimes = useMemo(() => {
        if (!hourlyPrices || hourlyPrices.length === 0) return [];
        const currentHour = new Date().getHours();
        let maxStart = null;
        if (deadlineOn) {
            const [dh] = deadline.split(':').map(Number);
            const target = dh > currentHour ? dh : 24 + dh;
            maxStart = Math.max(0, target - duration);
        }
        return calculateOptimalTimes(
            hourlyPrices, duration,
            { avoidNightHours: avoidNight, maxStartHour: maxStart, minStartHour: currentHour },
            hasTomorrowPrices
        ).slice(0, 3);
    }, [hourlyPrices, duration, avoidNight, deadlineOn, deadline, hasTomorrowPrices]);

    const worstTime = useMemo(() => {
        if (!hourlyPrices) return null;
        const all = calculateOptimalTimes(hourlyPrices, duration, {}, hasTomorrowPrices);
        return all[all.length - 1];
    }, [hourlyPrices, duration, hasTomorrowPrices]);

    const savings = estimateSavings(optimalTimes[0], worstTime, duration * power);
    const topSlot = optimalTimes[0] || null;
    const { insight, loading: aiLoading } = useAIInsight(
        hourlyPrices, 'custom', duration, topSlot, selectedRegion, hasTomorrowPrices
    );

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Körtid (timmar)</label>
                    <input
                        type="number" min="1" max="24" value={duration}
                        onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                        style={{ colorScheme: 'dark' }}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Effekt (kW)</label>
                    <input
                        type="number" min="0.1" max="100" step="0.1" value={power}
                        onChange={(e) => setPower(Math.max(0.1, parseFloat(e.target.value) || 1))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                        style={{ colorScheme: 'dark' }}
                    />
                </div>
                <div className="flex flex-col gap-2 justify-center">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" checked={avoidNight}
                            onChange={(e) => setAvoidNight(e.target.checked)}
                            className="w-4 h-4 rounded" />
                        <span className="text-sm text-white/70">Undvik natt</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" checked={deadlineOn}
                            onChange={(e) => setDeadlineOn(e.target.checked)}
                            className="w-4 h-4 rounded" />
                        <span className="text-sm text-white/70">Ange deadline</span>
                    </label>
                </div>
                {deadlineOn && (
                    <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">Klar senast</label>
                        <input
                            type="time" value={deadline}
                            onChange={(e) => setDeadline(e.target.value)}
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                            style={{ colorScheme: 'dark' }}
                        />
                    </div>
                )}
            </div>

            <PriceTimelineChart hourlyPrices={hourlyPrices} topWindow={topSlot} stats={stats} />

            <div className="bg-white/5 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <WrenchScrewdriverIcon className="h-5 w-5 text-orange-400" />
                    Anpassad apparat – Rekommendationer
                </h4>

                {optimalTimes.length === 0 ? (
                    <div className="text-center py-8">
                        <ExclamationTriangleIcon className="h-10 w-10 mx-auto text-yellow-400 mb-3" />
                        <p className="text-white/70">Inga tider hittades.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                            {optimalTimes.map((slot, i) => (
                                <RecommendationCard
                                    key={i} slot={slot} index={i}
                                    duration={duration} power={power}
                                    currencySymbol={currencySymbol} accentRgb="249,115,22"
                                />
                            ))}
                        </div>
                        {parseFloat(savings) > 0 && (
                            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 flex items-center gap-2">
                                <CurrencyEuroIcon className="h-5 w-5 text-orange-400 flex-shrink-0" />
                                <span className="text-orange-300 text-sm">
                                    Potentiell besparing: <strong>{savings} kr</strong> per körning
                                </span>
                            </div>
                        )}
                        <AIInsightPanel insight={insight} loading={aiLoading} />
                    </>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SmartSchedulerContent – main page layout
// ─────────────────────────────────────────────────────────────────────────────
const SmartSchedulerContent = () => {
    const [electricityData, setElectricityData] = useState({});
    const [loading,         setLoading]         = useState(true);
    const [error,           setError]           = useState(null);
    const [selectedRegion,  setSelectedRegion]  = useState('SE3');
    const [selectedTab,     setSelectedTab]     = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await axios.get('/api/prices');
                setElectricityData(res.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching pricing data:', err);
                setError('Kunde inte hämta elpriser. Försök igen senare.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        const timer = setInterval(fetchData, 15 * 60 * 1000);
        return () => clearInterval(timer);
    }, []);

    const todayPrices = useMemo(
        () => electricityData?.prices?.[selectedRegion]?.hourly || [],
        [electricityData, selectedRegion]
    );
    // Nord Pool publishes day-ahead prices at ~13:05 Stockholm time.
    // Ignore tomorrow prices before that – stale cache can otherwise leak yesterday's
    // "tomorrow" field (which is today's data) and confuse the scheduler.
    const isPriceReleaseTime = useMemo(() => {
        const swe = new Date(new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' }));
        return swe.getHours() > 13 || (swe.getHours() === 13 && swe.getMinutes() >= 5);
    }, []);

    const tomorrowPrices = useMemo(
        () => electricityData?.predictedPrices?.tomorrow?.[selectedRegion]?.hourly || [],
        [electricityData, selectedRegion]
    );
    const hasTomorrowPrices = false;
    const is15Min = useMemo(
        () => electricityData?.prices?.[selectedRegion]?.is15Min || false,
        [electricityData, selectedRegion]
    );
    // Collapse 15-minute resolution data (96 pts) into 24 hourly averages so
    // the scheduler durations and chart labels always operate on hourly units.
    const hourlyPrices = useMemo(() => {
        if (is15Min && todayPrices.length === 96) {
            return Array.from({ length: 24 }, (_, h) => {
                const slice = todayPrices.slice(h * 4, h * 4 + 4).filter(p => p != null);
                return slice.length > 0 ? slice.reduce((a, b) => a + b, 0) / slice.length : null;
            });
        }
        return todayPrices.slice(0, 24);
    }, [todayPrices, is15Min]);
    const stats = useMemo(() => analyzePrices(hourlyPrices), [hourlyPrices]);
    const currentPrice = hourlyPrices[new Date().getHours()];

    const TABS = [
        { id: 'ev',         label: 'Elbil',           icon: CarIcon },
        { id: 'dishwasher', label: 'Diskmaskin',       icon: HomeIcon },
        { id: 'laundry',    label: 'Tvätt',            icon: ArrowPathIcon },
        { id: 'pool',       label: 'Poolpump',         icon: WrenchScrewdriverIcon },
        { id: 'heating',    label: 'Uppvärmning / VP', icon: FireIcon },
        { id: 'hot_water',  label: 'Varmvatten',       icon: FireIcon },
        { id: 'custom',     label: 'Anpassad',         icon: SparklesOutlineIcon }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black">
                <div className="max-w-7xl mx-auto p-4 pt-20">
                    <div className="animate-pulse space-y-6">
                        <div className="h-10 bg-white/10 rounded w-1/3 mx-auto" />
                        <div className="h-40 bg-white/10 rounded" />
                        <div className="h-64 bg-white/10 rounded" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-7xl mx-auto p-4 pt-20">

                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 flex items-center justify-center gap-3">
                        <ClockIcon className="h-9 w-9 text-blue-400" />
                        Smart Schemaläggning
                    </h1>
                    <p className="text-white/55 max-w-2xl mx-auto">
                        Hitta det billigaste och stabilaste tidsfönstret för dina apparater – baserat på
                        live Nord Pool-priser med AI-analys.
                    </p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-red-300">{error}</p>
                    </div>
                )}

                {/* Region selector */}
                <div className="mb-8">
                    <p className="text-sm text-white/50 mb-3">Välj elområde</p>
                    <div className="flex flex-wrap gap-2">
                        {['SE1', 'SE2', 'SE3', 'SE4'].map((r) => (
                            <button
                                key={r}
                                onClick={() => setSelectedRegion(r)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                    selectedRegion === r
                                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                                        : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                                }`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Price overview */}
                {stats && (
                    <div className="mb-8 bg-white/5 rounded-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-5">
                        <div>
                            <p className="text-xs text-white/40 mb-1">Nu ({selectedRegion})</p>
                            <p className="text-2xl font-bold text-white">
                                {currentPrice !== null && currentPrice !== undefined
                                    ? currentPrice.toFixed(1) + ' öre'
                                    : '–'}
                            </p>
                            <p className="text-xs text-white/30">per kWh inkl. moms</p>
                        </div>
                        <div>
                            <p className="text-xs text-white/40 mb-1">Lägsta</p>
                            <p className="text-xl font-semibold text-green-400">{stats.min.toFixed(1)} öre</p>
                        </div>
                        <div>
                            <p className="text-xs text-white/40 mb-1">Snitt</p>
                            <p className="text-xl font-semibold text-yellow-400">{stats.avg.toFixed(1)} öre</p>
                        </div>
                        <div>
                            <p className="text-xs text-white/40 mb-1">Högsta</p>
                            <p className="text-xl font-semibold text-red-400">{stats.max.toFixed(1)} öre</p>
                        </div>

                    </div>
                )}

                {/* Scheduler tabs */}
                <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
                    <Tab.List className="flex gap-1 rounded-xl bg-white/10 p-1 mb-8 overflow-x-auto">
                        {TABS.map((tab) => (
                            <Tab
                                key={tab.id}
                                className={({ selected }) =>
                                    'flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium rounded-lg transition-all ' +
                                    (selected
                                        ? 'bg-white/20 text-white shadow'
                                        : 'text-white/55 hover:text-white hover:bg-white/10')
                                }
                            >
                                <tab.icon className="h-4 w-4" />
                                {tab.label}
                            </Tab>
                        ))}
                    </Tab.List>

                    <Tab.Panels>
                        <Tab.Panel>
                            <EVChargingScheduler
                                hourlyPrices={hourlyPrices}
                                selectedRegion={selectedRegion}
                                currencySymbol="öre"
                                hasTomorrowPrices={hasTomorrowPrices}
                                stats={stats}
                            />
                        </Tab.Panel>
                        {['dishwasher', 'laundry', 'pool', 'heating', 'hot_water'].map((key) => (
                            <Tab.Panel key={key}>
                                <ApplianceScheduler
                                    hourlyPrices={hourlyPrices}
                                    selectedRegion={selectedRegion}
                                    currencySymbol="öre"
                                    applianceKey={key}
                                    hasTomorrowPrices={hasTomorrowPrices}
                                    stats={stats}
                                />
                            </Tab.Panel>
                        ))}
                        <Tab.Panel>
                            <CustomApplianceScheduler
                                hourlyPrices={hourlyPrices}
                                selectedRegion={selectedRegion}
                                currencySymbol="öre"
                                hasTomorrowPrices={hasTomorrowPrices}
                                stats={stats}
                            />
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>

                {/* General tips */}
                <div className="mt-12 bg-white/5 rounded-2xl p-6">
                    <h3 className="text-base font-semibold text-white mb-5 flex items-center gap-2">
                        <SparklesIcon className="h-5 w-5 text-purple-400" />
                        Generella energitips
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div className="flex items-start gap-3">
                            <MoonIcon className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-white">Nattetid (22–06)</p>
                                <p className="text-xs text-white/45 mt-1">
                                    Lägst belastning och ofta lägst pris – perfekt för elbil och varmvattenberedare.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <SunIcon className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-white">Dagtid (07–19)</p>
                                <p className="text-xs text-white/45 mt-1">
                                    Höglasttid – undvik energiintensiva apparater om möjligt.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <CalendarDaysIcon className="h-5 w-5 text-purple-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-white">Planera i förväg</p>
                                <p className="text-xs text-white/45 mt-1">
                                    Morgondagens priser publiceras ca 13:05. Schemalägg under kvällen inför natten.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Root export
// ─────────────────────────────────────────────────────────────────────────────
const SmartScheduler = () => (
    <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.reload()}>
        <SmartSchedulerContent />
    </ErrorBoundary>
);

export default React.memo(SmartScheduler);
